import {ApplicationUserRole, OperationType} from "../api";

const roleTranslations: Record<ApplicationUserRole, string> = {
    [ApplicationUserRole.Admin]: "Administrator",
    [ApplicationUserRole.UserManager]: "Zarządzanie użytkownikami",
    [ApplicationUserRole.CanReadWarehouse]: "Dostęp do magazynów",
    [ApplicationUserRole.CanUpdateContractors]: "Zarządzanie kontrahentami",
    [ApplicationUserRole.ManageUsers]: "Zarządzanie użytkownikami",
    [ApplicationUserRole.ReadWarehouses]: "Podgląd magazynów",
    [ApplicationUserRole.ManageWarehouses]: "Zarządzanie magazynami",
    [ApplicationUserRole.ReadStock]: "Podgląd stanów magazynowych",
    [ApplicationUserRole.ManageStock]: "Zarządzanie stanami magazynowymi",
    [ApplicationUserRole.ReadProducts]: "Podgląd produktów",
    [ApplicationUserRole.ManageProducts]: "Zarządzanie produktami",
    [ApplicationUserRole.ReadProduction]: "Podgląd produkcji",
    [ApplicationUserRole.ManageProduction]: "Zarządzanie produkcją",
    [ApplicationUserRole.ReadAnalytics]: "Podgląd analiz",
    [ApplicationUserRole.ManageAnalytics]: "Zarządzanie analizami",
    [ApplicationUserRole.ReadContractors]: "Podgląd kontrahentów",
    [ApplicationUserRole.ManageContractors]: "Zarządzanie kontrahentami",
    [ApplicationUserRole.ReadMeetings]: "Podgląd spotkań",
    [ApplicationUserRole.ManageMeetings]: "Zarządzanie spotkaniami",
    [ApplicationUserRole.ManageSettings]: "Zarządzanie ustawieniami",
    [ApplicationUserRole.ReadOperations]: "Podgląd operacji",
    [ApplicationUserRole.ManageOperations]: "Zarządzanie operacjami",
    [ApplicationUserRole.ReadInventory]: "Podgląd inwentaryzacji",
    [ApplicationUserRole.ManageInventory]: "Zarządzanie inwentaryzacją",
    [ApplicationUserRole.NexoSync]: "Synchronizacja Nexo"
};

const compatibilityRoleAliases: Partial<Record<ApplicationUserRole, ApplicationUserRole[]>> = {
    [ApplicationUserRole.ManageUsers]: [ApplicationUserRole.UserManager],
    [ApplicationUserRole.UserManager]: [ApplicationUserRole.ManageUsers],
    [ApplicationUserRole.ReadWarehouses]: [ApplicationUserRole.CanReadWarehouse],
    [ApplicationUserRole.CanReadWarehouse]: [ApplicationUserRole.ReadWarehouses],
    [ApplicationUserRole.ManageContractors]: [ApplicationUserRole.CanUpdateContractors],
    [ApplicationUserRole.CanUpdateContractors]: [ApplicationUserRole.ManageContractors],
};

const hiddenCompatibilityRoles = new Set<ApplicationUserRole>([
    ApplicationUserRole.UserManager,
    ApplicationUserRole.CanReadWarehouse,
    ApplicationUserRole.CanUpdateContractors,
]);

export const MANAGE_USERS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageUsers, ApplicationUserRole.UserManager];
export const READ_WAREHOUSES_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadWarehouses, ApplicationUserRole.CanReadWarehouse];
export const MANAGE_WAREHOUSES_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageWarehouses];
export const READ_STOCK_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadStock];
export const READ_PRODUCTS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadProducts];
export const MANAGE_PRODUCTS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageProducts];
export const READ_PRODUCTION_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadProduction];
export const MANAGE_PRODUCTION_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageProduction];
export const READ_ANALYTICS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadAnalytics];
export const MANAGE_ANALYTICS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageAnalytics];
export const READ_CONTRACTORS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadContractors, ApplicationUserRole.ManageContractors, ApplicationUserRole.CanUpdateContractors];
export const MANAGE_CONTRACTORS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageContractors, ApplicationUserRole.CanUpdateContractors];
export const READ_OPERATIONS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadOperations];
export const MANAGE_OPERATIONS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageOperations];
export const READ_INVENTORY_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadInventory];
export const MANAGE_INVENTORY_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageInventory];
export const MANAGE_SETTINGS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageSettings];

const allAuthScopes = [
    "manage:users",
    "read:warehouses",
    "manage:warehouses",
    "read:stock",
    "manage:stock",
    "read:products",
    "manage:products",
    "read:production",
    "manage:production",
    "read:analytics",
    "manage:analytics",
    "read:contractors",
    "manage:contractors",
    "read:meetings",
    "manage:meetings",
    "manage:settings",
    "read:operations",
    "manage:operations",
    "read:inventory",
    "manage:inventory",
    "nexo:sync",
    "email",
    "profile"
];

const assignableApplicationUserRoles = Object.values(ApplicationUserRole).filter(role =>
    role !== ApplicationUserRole.Admin && !hiddenCompatibilityRoles.has(role)
);

const operationTypeTranslations: { operation: OperationType, translation: string, isReservation?: boolean }[] = [
    {operation: OperationType.Create, translation: "utworzył(a)"},
    {operation: OperationType.Edit, translation: "zmienił(a)"},
    {operation: OperationType.Delete, translation: "usunął(a)"},
    {operation: OperationType.Assign, translation: "przypisał(a)"},
    {operation: OperationType.Assign, translation: "zarezerwował(a)", isReservation: true}
];

export const normalizeApplicationUserRoles = (
    roles?: ApplicationUserRole[] | null
): ApplicationUserRole[] => {
    const normalizedRoles = new Set<ApplicationUserRole>(roles ?? []);

    for (const role of roles ?? []) {
        const aliases = compatibilityRoleAliases[role] ?? [];
        aliases.forEach(alias => normalizedRoles.add(alias));
    }

    if (normalizedRoles.has(ApplicationUserRole.Admin)) {
        normalizedRoles.add(ApplicationUserRole.UserManager);
        normalizedRoles.add(ApplicationUserRole.CanReadWarehouse);
        normalizedRoles.add(ApplicationUserRole.CanUpdateContractors);
    }

    return Array.from(normalizedRoles);
};

export const getAssignableApplicationUserRoles = (): ApplicationUserRole[] => assignableApplicationUserRoles;

export const getAuth0ScopesForFrontend = (): string => allAuthScopes.join(" ");

const applicationUserRoleTranslationHelper = (role: ApplicationUserRole) =>
    roleTranslations[role] || '';

const operationTypeTranslationHelper = (operation: OperationType, isReservation?: boolean) => {
    return operationTypeTranslations.find(x => x.operation === operation && x.isReservation === isReservation)?.translation || '';
};

export default applicationUserRoleTranslationHelper;

export {operationTypeTranslationHelper};
